import {Component, Input} from '@angular/core';
import {Unit} from '../../dashboard/monitoring/unit';
import {TranslateService} from '@ngx-translate/core';
import {IndicatorsViewModel} from '../../shared/card-view/indicators-view/indicators-view.model';
import {UnitIcon} from '../../dashboard/monitoring/unit-icon';

@Component({
  selector: 'app-unit-card-indicators',
  templateUrl: './unit-card-indicators.component.html',
})
export class UnitCardIndicatorsComponent {
  indicators: Array<IndicatorsViewModel> = [];

  caseNullOrTrueOrFalse(unit: Unit, fieldName: string, valueIfTrue: string, valueIfFalse: string, valueIfNull: string): string {
    if (!unit || !unit.lastMsgUnit || unit.lastMsgUnit[fieldName] === null) {
      return valueIfNull;
    }
    return unit.lastMsgUnit[fieldName] ? valueIfTrue : valueIfFalse;
  }

  caseSpeedMode(unit: Unit, valueIfEco: string, valueIfDrive: string, valueIfSport: string, valueIfUnknown: string): string {
    if (!unit || !unit.lastMsgUnit || unit.lastMsgUnit.speed_mode === null) {
      return valueIfUnknown;
    }
    switch (unit.lastMsgUnit.speed_mode) {
      case 'eco':
        return valueIfEco;
      case 'drive':
        return valueIfDrive;
      case 'sport':
        return valueIfSport;
    }
    return valueIfUnknown;
  }

  protected speedIndicator(unit: Unit): IndicatorsViewModel {
    const speed = unit && unit.lastMsgUnit && unit.lastMsgUnit.speed;

    return {
      title: this.isValueDefined(speed) ? speed + ' ' + this.translates.instant('km/h') : this.translates.instant('n/i'),
      iconClass: 'icon icon-unit-card icon-unit-card-unit-speed' +
        (speed === 0 ? ' default' : this.isValueDefined(speed) ? ' active' : ' inactive'),
      value: this.isValueDefined(speed) ? speed + ' ' + this.translates.instant('km/h') : this.translates.instant('n/i'),
      isVisible: this.isValueDefined(speed),
    };
  }

  protected brakeIsOnIndicator(unit: Unit): IndicatorsViewModel {
    return {
      title: this.translates.instant(
        this.caseNullOrTrueOrFalse(unit, 'brake_is_on', 'Brake: ON', 'Brake: OFF', 'Brake: no information')
      ),
      iconClass: 'icon icon-unit-card icon-unit-card-unit-brake' +
        this.caseNullOrTrueOrFalse(unit, 'brake_is_on', ' on', ' off', ' inactive'),
      value: this.translates.instant(
        this.caseNullOrTrueOrFalse(unit, 'brake_is_on', 'On', 'Off', 'n/i')
      ),
      isVisible: this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.brake_is_on),
    };
  }

  protected protectIndicator(unit: Unit): IndicatorsViewModel {
    return {
      title: this.translates.instant(
        this.caseNullOrTrueOrFalse(unit, 'protect', 'Security mode: is on', 'Security mode: is off', 'n/i')
      ),
      iconClass: 'icon icon-unit-card icon-unit-protect' +
        this.caseNullOrTrueOrFalse(unit, 'protect', ' on', ' off', ''),
      value: this.translates.instant(this.caseNullOrTrueOrFalse(unit, 'protect', 'On', 'Off', 'n/i')),
      isVisible: this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.protect),
    };
  }

  protected chargeIndicator(unit: Unit): IndicatorsViewModel {
    return {
      title: unit.lastMsgUnit && this.isValueDefined(unit.lastMsgUnit.charge)
        ? unit.lastMsgUnit.charge + '%' : this.translates.instant('n/i'),
      iconClass: UnitIcon.unitChargeStatusIconClass(unit.chargeStatus),
      value: unit.lastMsgUnit && this.isValueDefined(unit.lastMsgUnit.charge)
        ? unit.lastMsgUnit.charge + '%' : this.translates.instant('n/i'),
      isVisible: this.hasValue(unit.charge) && this.hasValue(unit.chargeStatus),
    };
  }

  protected voltageIndicator(unit: Unit): IndicatorsViewModel {
    return {
      title: unit && unit.lastMsgUnit && unit.lastMsgUnit.voltage !== null ?
        unit.lastMsgUnit.voltage + ' ' + this.translates.instant('voltage_abbreviation') : '',
      iconClass: 'icon ic-voltage',
      value: unit && unit.lastMsgUnit && unit.lastMsgUnit.voltage !== null ?
        unit.lastMsgUnit.voltage + ' ' + this.translates.instant('voltage_abbreviation') : '',
      isVisible: unit.lastMsgUnit && unit.lastMsgUnit.voltage !== null &&
        unit.unit_type_name && !(
          unit.unit_type_name === Unit.UNIT_TYPE_CHARGING
          || unit.unit_type_name === Unit.UNIT_TYPE_ELECTRIC_SCOOTER
        ),
    };
  }

  protected lockIndicator(unit: Unit): IndicatorsViewModel {
    return {
      title: this.translates.instant(
        this.caseNullOrTrueOrFalse(unit, 'lock_is_closed', 'Lock: is closed', 'Lock: is opened', 'Lock: no information')
      ),
      iconClass: 'icon icon-unit-card icon-unit-card-unit-lock' +
        this.caseNullOrTrueOrFalse(unit, 'lock_is_closed', ' closed', ' opened', ' inactive'),
      value: this.translates.instant(
        this.caseNullOrTrueOrFalse(unit, 'lock_is_closed', 'Closed', 'Opened', 'n/i')
      ),
      isVisible: this.isValueDefined(unit.hwModel)
        && this.isValueDefined(unit.hwModel.lock_type)
        && unit.hwModel.lock_type !== 'without-lock'
        && this.isValueDefined(unit.lastMsgUnit)
        && this.isValueDefined(unit.lastMsgUnit.lock_is_closed)
      ,
    };
  }

  @Input() set unit(unit: Unit) {
    this.indicators = [
      {
        title: unit && unit.lastMsgUnit && unit.lastMsgUnit.code_error ? 'Error code' + unit.lastMsgUnit.code_error : '',
        iconClass: 'icon icon-unit-card icon-unit-indicator-code-error',
        value: unit && unit.lastMsgUnit && unit.lastMsgUnit.code_error ? 'ERR: ' + unit.lastMsgUnit.code_error : '',
        isVisible: unit && unit.lastMsgUnit && unit.lastMsgUnit.code_error && unit.lastMsgUnit.code_error > 0,
      },
      this.chargeIndicator(unit),
      {
        title: this.translates.instant('Service mode: is on'),
        iconClass: 'icon icon-unit-card icon-unit-indicator-service-mode on',
        value: this.translates.instant('On'),
        isVisible: this.isValueDefined(unit.lastMsgUnit) && unit.lastMsgUnit.service_mode_is_on,
      },
      this.protectIndicator(unit),
      {
        title: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'siren_is_on', 'Siren on', 'Siren off', 'n/i')
        ),
        iconClass: 'icon icon-unit-card icon-unit-indicator-siren' +
          this.caseNullOrTrueOrFalse(unit, 'siren_is_on', ' on', ' off', ''),
        value: this.translates.instant(this.caseNullOrTrueOrFalse(unit, 'siren_is_on', 'On', 'Off', 'n/i')),
        isVisible: this.isValueDefined(unit.lastMsgUnit) && unit.unit_type_name ===  Unit.UNIT_TYPE_ELECTRIC_SCOOTER
          && this.isValueDefined(unit.lastMsgUnit.siren_is_on)
      },
      this.lockIndicator(unit),
      {
        title: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'head_is_on', 'Head: is on', 'Head: is off', 'Head: no information')
        ),
        iconClass: 'icon icon-unit-card icon-unit-card-unit-head' +
          this.caseNullOrTrueOrFalse(unit, 'head_is_on', ' on', ' off', ' inactive'),
        value: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'head_is_on', 'On', 'Off', 'n/i')
        ),
        isVisible: this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.head_is_on),
      },
      this.brakeIsOnIndicator(unit),
      {
        title: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'flashlight_is_on', 'Flashlight: is on', 'Flashlight: is off', 'Flashlight: no information')
        ),
        iconClass: 'icon icon-unit-card icon-unit-card-unit-flashlight' +
          this.caseNullOrTrueOrFalse(unit, 'flashlight_is_on', ' on', ' off', ' inactive'),
        value: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'flashlight_is_on', 'On', 'Off', 'n/i')
        ),
        isVisible: this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.flashlight_is_on),
      },
      {
        title: this.translates.instant(
          this.caseSpeedMode(unit, 'Speed mode: eco', 'Speed mode: drive', 'Speed mode: sport', 'Speed mode: no information')
        ),
        iconClass: 'icon icon-unit-card icon-unit-card-unit-speed-mode ' +
          this.caseSpeedMode(unit, 'eco', 'drive', 'sport', 'inactive'),
        value: this.translates.instant(
          this.caseSpeedMode(unit, 'ECO', 'DRIVE', 'SPORT', 'n/i')
        ),
        isVisible: this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.speed_mode) ,
      },
      this.voltageIndicator(unit),
      this.speedIndicator(unit),
      {
        title: this.translates.instant(
          this.caseNullOrTrueOrFalse(
            unit,
            'battery_lock_is_closed',
            'Battery lock: Closed',
            'Battery lock: Open',
            'Battery lock: no information'
          )
        ),
        iconClass: 'icon icon-unit-card icon-unit-card-battery-lock-mode ' +
          this.caseNullOrTrueOrFalse(unit, 'battery_lock_is_closed', ' closed', ' opened', ' inactive'),
        value: this.translates.instant(
          this.caseNullOrTrueOrFalse(unit, 'battery_lock_is_closed', 'Closed', 'Opened', 'n/i')
        ),
        isVisible: unit.hwModel && unit.hwModel.battery_mount_type && unit.hwModel.battery_mount_type === 'removable'
        && this.isValueDefined(unit.lastMsgUnit) && this.isValueDefined(unit.lastMsgUnit.battery_lock_is_closed) ,
      },
    ];
  }

  isValueDefined(value: any) {
    return value !== null && value !== undefined;
  }

  hasValue(value: any) {
    return this.isValueDefined(value) && value !== '';
  }

  constructor(
    protected translates: TranslateService,
  ) {
  }
}
